<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import type { SalesContactPageWithMarketStylesFormProps } from './types';
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';
  import { resizeObserver } from '$lib/helpers/browser/resize-observer';

  export let entryId: SalesContactPageWithMarketStylesFormProps['entryId'];
  export let heroSection: SalesContactPageWithMarketStylesFormProps['heroSection'];
  export let leadFormCustomComponent: SalesContactPageWithMarketStylesFormProps['leadFormCustomComponent'];
  export let trustPilot: SalesContactPageWithMarketStylesFormProps['trustPilot'];
  export let existingCustomer: SalesContactPageWithMarketStylesFormProps['existingCustomer'];

  let leadFormContainer: HTMLElement;
  let letsGetYouSetUpContainer: HTMLElement;
  let backgroundElement: HTMLElement;
  let unobserveResizeObserver: any;

  const DELAY = 1000;

  function setBackgroundHeight() {
    backgroundElement.style.setProperty('--lets-get-you-height', `${letsGetYouSetUpContainer.clientHeight}px`);
  }

  onMount(() => {
    unobserveResizeObserver = resizeObserver(
      letsGetYouSetUpContainer,
      () => {
        setBackgroundHeight();
      },
      false
    );

    // ios trick to fix the keyboard issue
    const input = document.createElement('input');
    input.setAttribute('type', 'text');
    input.setAttribute('style', 'position: fixed; top: -100px; left: -100px;');
    document.body.appendChild(input);
    input.focus();

    const inputField = leadFormContainer.querySelectorAll('.marketInput')[0] as HTMLInputElement;
    inputField.click();

    // clean up ios trick
    setTimeout(() => {
      document.body.removeChild(input);
    }, DELAY);
  });

  onDestroy(() => {
    if (unobserveResizeObserver) {
      unobserveResizeObserver();
    }
  });
</script>

<div class="background" bind:this={backgroundElement} />
<section
  class="SalesContactPageWithMarketStylesForm SalesContactPageWithMarketStylesForm-{entryId}-box-properties content"
  data-entry-id={entryId}
>
  <div class="letsGetYouSetUpContainer" bind:this={letsGetYouSetUpContainer}>
    <DynamicChildrenComponent children={[heroSection]} />
  </div>
  <div class="leadFormContainer" bind:this={leadFormContainer}>
    <DynamicChildrenComponent children={[leadFormCustomComponent]} />
  </div>
  <div class="trustPilotAndExistingCustomers">
    {#if trustPilot}
      <div class="trustPilotContainer">
        <DynamicChildrenComponent children={[trustPilot]} />
      </div>
    {/if}
    <div class="existingCustomerContainer">
      <DynamicChildrenComponent children={[existingCustomer]} />
    </div>
  </div>
</section>

<style lang="scss">
  // add nav width
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/core/config/media_queries' as mediaQueries;

  @layer custom-components {
    .background {
      --color-light-blue: #c9e8f5;
      --lets-get-you-height: auto;

      position: absolute;
      top: 73px;
      width: 100%;
      height: var(--lets-get-you-height);
      background-color: var(--color-light-blue);
      z-index: 1;
    }

    .SalesContactPageWithMarketStylesForm {
      display: grid;
      position: relative;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 0.5fr 1fr 0fr;
      grid-auto-rows: 0.5fr;
      gap: 0px 0px;
      grid-auto-flow: row;
      grid-template-areas:
        'block-1 block-4'
        'block-2 block-4'
        'block-3 block-4';
    }

    .leadFormContainer {
      grid-area: block-4;
      z-index: 2;
      position: relative;

      :global(.sq-custom-sc-lead-form) {
        overflow: visible;
      }

      :global(.LeadFormSection) {
        background-color: var(--color-white);
        border: 1px solid var(--color-gray-15);
        border-width: 1px;
        border-radius: 24px;
        padding: var(--space-40);
        width: 100%;
        max-width: 585px;
        margin: 0 auto;

        :global(.content) {
          width: 100%;
          max-width: none !important;
        }

        :global(.VisibleField .RichTextField) {
          --color: var(--color-gray);
          --line-height: var(--line-height-24);
          --font-size: var(--font-size-14);
          margin-top: -4px;
          margin-bottom: var(--space-10);
        }
      }
    }

    .letsGetYouSetUpContainer {
      grid-area: block-1;
      position: relative;

      :global(.BoxComponent) {
        z-index: 2;
      }

      :global(p) {
        font-size: var(--font-size-body-large);
        line-height: var(--line-height-body-large);
      }
    }

    .trustPilotAndExistingCustomers {
      grid-area: block-2;
    }

    .trustPilotContainer {
      padding-top: var(--space-50);

      :global(.LinkComponent--text) {
        --font-size: var(--font-size-body-large);
        --line-height: var(--line-height-body-large);
      }
    }

    .letsGetYouSetUpContainer {
      :global(h1) {
        color: var(--color-gray-99);
        margin-bottom: 0;
      }
      :global(p) {
        margin-bottom: 0;
      }
    }

    @include mediaQueries.until('large') {
      .existingCustomerContainer {
        :global(.BoxComponent) {
          margin-bottom: 95px;
          padding-right: var(--space-10);
        }

        :global(p) {
          line-height: 24px;
        }
      }

      .letsGetYouSetUpContainer {
        :global(h1) {
          margin-right: var(--space-80);
        }
      }
    }

    @include mediaQueries.until('medium') {
      .SalesContactPageWithMarketStylesForm {
        display: block;
      }

      .trustPilotContainer {
        padding-top: 0;
      }

      .leadFormContainer {
        :global(.LeadFormSection) {
          background-color: var(--color-white);
          border: none;
          padding: var(--space-30) 0;
          width: 100%;
          max-width: none;
        }

        :global(.LeadFormSection .VisibleField .RichTextField) {
          margin-bottom: var(--space-20);
        }
      }

      .letsGetYouSetUpContainer {
        :global(p) {
          font-size: 16px;
          line-height: 24px;
        }

        :global(h1) {
          margin-bottom: 0;
          font-size: 46px;
          line-height: 54px;
        }
      }

      .existingCustomerContainer {
        :global(.BoxComponent) {
          margin-bottom: var(--space-40);
        }

        :global(h6) {
          font-size: 20px;
        }

        :global(p) {
          font-size: 16px;
        }
      }
    }

    @include mediaQueries.at('base') {
      .background {
        --lets-get-you-height: 346px;
      }
    }
    @include mediaQueries.at('extra-small') {
      .background {
        --lets-get-you-height: 318px;
      }
    }
    @include mediaQueries.at('small') {
      .background {
        --lets-get-you-height: 208px;
      }
    }
    @include mediaQueries.at('medium') {
      .background {
        --lets-get-you-height: 470px;
      }
    }
    @include mediaQueries.at('large') {
      .background {
        --lets-get-you-height: 383px;
      }
    }
  }

  :global(
    .PublicWebNav nav.MobileNav,
    .PublicWebNav nav.DesktopNav,
    .PublicWebNavV2 .GlobalNav .MobileNav,
    .PublicWebNavV2 .GlobalNav .DesktopNav
  ) {
    padding-left: 0 !important;
    padding-right: 0 !important;
    margin-left: auto;
    margin-right: auto;
    width: var(--container-width-current);
    justify-content: left !important;
  }

  :global(.PublicWebNav .HomeLink, .PublicWebNavV2 .HomeLink) {
    padding-left: 0 !important;
  }
</style>
